import { useContext, createContext, useReducer, useCallback, useMemo, useRef, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';

/**
 * Variable checking the server environment runtime.
 */ const IS_SERVER_ENVIRONMENT = typeof window === "undefined";
/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB",
    "fr-BE",
    "fr-FR",
    "nl-BE"
];
new Set(LANGUAGE_TAGS_VALUES);
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>{
    return `❌ An error occurred: ${cause}.\n✅ To solve the issue: ${solution}.`;
};
/**
 * A factory to uniformize the way global module variables are named.
 * It prefixes each module by the Unified Frontend {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/blob/6d544d0fb00088c7af670924bb25150128a45285/libraries/registry/types/index.ts#L139 capability code}.
 * @param module - The module name.
 * @param component - Optional component name to append.
 * @returns The global key name derivated from the input (used to scope global states linked to a module).
 */ const createGlobalKey = (module, component)=>{
    return `__UFRN_${module.toUpperCase()}${"" }__`;
};
createGlobalKey("BUILD_FINGERPRINTS");
const IsIntantiatedContext = /*#__PURE__*/ createContext(false);
/**
 * A higher-order-factory-like helper to enforce a single component instance in the tree.
 * @param componentName The component name.
 * @param Component The targetted React component to decorate with the singleton behavior (most of the time, Provider-like component).
 * @returns The decorated component with singleton behavior.
 */ const createSingletonComponent = (componentName, Component)=>{
    return (props)=>{
        const isInstantiated = useContext(IsIntantiatedContext);
        if (isInstantiated) {
            console.error(createErrorMessage({
                cause: `The \`${componentName}\` component cannot be instantiated more than once`,
                solution: `The singleton behavior has been automatically enforced by skipping the nested \`${componentName}\` element creation. To remove the log and prevent any introduced side effect(s), please remove any \`${componentName}\` component instance in your component tree outside the sandbox entry point.\nIf the log still persists, make sure the sandbox entry point is not included in your production bundle`
            }));
            return props.children;
        }
        return /*#__PURE__*/ jsx(IsIntantiatedContext.Provider, {
            value: true,
            children: /*#__PURE__*/ jsx(Component, {
                ...props
            })
        });
    };
};
/**
 * Error object has no enumerable properties by default, so information such as name, message are not strignify in JSON.
 * This function converts an error object to a plain object with enumerable properties.
 */ const deserializeErrors = (obj)=>Object.entries(obj).reduce((acc, [key, value])=>{
        const error = new Error();
        Object.keys(value).forEach((key)=>{
            if (value[key]) {
                error[key] = value[key];
            }
        });
        acc[key] = error;
        return acc;
    }, {});
/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = useContext(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};

const useRuntimeContext = (moduleName, context)=>useSafeContext({
        package: "@aviv-unified-frontend/runtime",
        module: moduleName
    }, context);

const GLOBAL_KEY = createGlobalKey("fetcher");

const getInitialState = ({ data, errors })=>{
    const result = {};
    const allKeys = new Set([
        ...data.keys(),
        ...errors.keys()
    ]);
    allKeys.forEach((key)=>{
        const error = errors.get(key);
        if (error) {
            result[key] = {
                status: "rejected",
                error
            };
            return;
        }
        const item = data.get(key);
        if (item) {
            result[key] = {
                status: "fulfilled",
                data: item
            };
        }
    });
    return result;
};
const reducer = (statebykey, action)=>{
    const { type, payload } = action;
    switch(type){
        case "pending":
            {
                return {
                    ...statebykey,
                    [payload.key]: {
                        status: type
                    }
                };
            }
        case "fulfilled":
            {
                return {
                    ...statebykey,
                    [payload.key]: {
                        status: type,
                        data: payload.data
                    }
                };
            }
        case "rejected":
            {
                return {
                    ...statebykey,
                    [payload.key]: {
                        status: type,
                        error: payload.error
                    }
                };
            }
    }
};
const useFetcherReducer = (cache, mode)=>{
    const [stateByKey, dispatch] = useReducer(reducer, getInitialState({
        data: cache.data,
        errors: cache.errors
    }));
    const dispatchPending = useCallback((key)=>{
        cache.pendingKeys.add(key);
        dispatch({
            type: "pending",
            payload: {
                key
            }
        });
    }, [
        cache.pendingKeys
    ]);
    const dispatchFulfilled = useCallback((key, data)=>{
        cache.pendingKeys.delete(key);
        dispatch({
            type: "fulfilled",
            payload: {
                key,
                data
            }
        });
    }, [
        cache.pendingKeys
    ]);
    const dispatchRejected = useCallback((key, error)=>{
        cache.pendingKeys.delete(key);
        dispatch({
            type: "rejected",
            payload: {
                key,
                error
            }
        });
    }, [
        cache.pendingKeys
    ]);
    const hasPendingKey = useCallback((key)=>{
        return cache.pendingKeys.has(key);
    }, [
        cache.pendingKeys
    ]);
    /**
   * Returns the current state for a given cache key, with mode-aware default behavior.
   *
   * During SSR prefetch phase (mode="prefetch"), we return an empty object (no status)
   * instead of { status: "pending" }. This prevents parent components from showing
   * loading states, which would block rendering of their children.
   *
   * Without this fix, nested useFetcher hooks behind loading states would never be
   * discovered during prefetch traversal, causing:
   * 1. Missing data prefetching for nested components
   * 2. Flickering on client-side hydration (loading → data appears)
   *
   * By returning no status in prefetch mode, components render their children,
   * allowing the full component tree to be traversed and all useFetcher calls collected.
   */ const getStateByKey = useCallback((key)=>{
        const defaultState = mode === "prefetch" ? {} : {
            status: "pending"
        };
        return stateByKey[key] ?? defaultState;
    }, [
        stateByKey,
        mode
    ]);
    return useMemo(()=>({
            getStateByKey,
            dispatchPending,
            dispatchFulfilled,
            dispatchRejected,
            hasPendingKey
        }), [
        getStateByKey,
        dispatchPending,
        dispatchFulfilled,
        dispatchRejected,
        hasPendingKey
    ]);
};

/**
 * Create the fetcher context service (mainly for cache management).
 * @param initialCache - The initial data to hydrate the cache instance with.
 * @param options - Additional options including rendering mode.
 * @returns The fetcher context instance with the needed methods to manage it.
 */ const createFetcherContext = (initialCache = {
    data: [],
    promises: [],
    errors: []
}, options)=>{
    const cache = {
        data: new Map(initialCache.data),
        promises: new Map(initialCache.promises),
        errors: new Map(initialCache.errors),
        pendingKeys: new Set()
    };
    const cacheKeys = Object.keys(cache);
    return {
        cache,
        mode: options?.mode || "render",
        flush (key) {
            cacheKeys.forEach((cacheKey)=>{
                cache[cacheKey].delete(key);
            });
        },
        flushAll () {
            cacheKeys.forEach((cacheKey)=>{
                cache[cacheKey].clear();
            });
        }
    };
};
const Context = /*#__PURE__*/ createContext(null);
const defaultContext = createFetcherContext({
    // The initial cache is directly computed while creating context to not let the consumer deals with `FetcherContext.Provider`
    // app wrapping. Browser-side, the in-memory cache is hydrated from prefetched data injected server-side as a global DOM variable.
    data: IS_SERVER_ENVIRONMENT ? [] : Object.entries(window[GLOBAL_KEY]?.data ?? {}),
    promises: [],
    errors: IS_SERVER_ENVIRONMENT ? [] : Object.entries(deserializeErrors(window[GLOBAL_KEY]?.errors ?? {}))
});
const FetcherProvider = createSingletonComponent("FetcherProvider", ({ children, context = defaultContext })=>{
    // The following line aims to stabilize the context reference to ensure the global cache is processed only once.
    const contextRef = useRef(context);
    const { getStateByKey, dispatchPending, dispatchFulfilled, dispatchRejected, hasPendingKey } = useFetcherReducer(contextRef.current.cache, contextRef.current.mode);
    const value = useMemo(()=>({
            context: contextRef.current,
            getStateByKey,
            hasPendingKey,
            dispatchPending,
            dispatchFulfilled,
            dispatchRejected
        }), [
        getStateByKey,
        dispatchPending,
        dispatchFulfilled,
        dispatchRejected,
        hasPendingKey
    ]);
    return /*#__PURE__*/ jsx(Context.Provider, {
        value: value,
        children: children
    });
});
const useFetcherContext = ()=>useRuntimeContext("fetcher", Context);

const DEFAULT_PREFETCHING_OPTION = true;
const DEFAULT_STRATEGY_OPTION = "cache-first";
/**
 * Fetch data in an universal way (server-side data prefetching with client-side hydration)
 * @param key The key to identifying a given resource. Used internally by the global cache, the key is required to enable cache reuse/deduplication/invalidation transparently whenever targeting the same API endpoint.
 * @param fetcher A promise-based method that implements the consumer fetching logic.
 * @param options Object to customize the fetching and caching behaviors.
 * @returns The result including data and its retrieval state (loading and error states).
 */ const useFetcher = (key, fetcher, options = {
    prefetching: DEFAULT_PREFETCHING_OPTION,
    strategy: DEFAULT_STRATEGY_OPTION
})=>{
    const prefetching = options.prefetching ?? DEFAULT_PREFETCHING_OPTION;
    const strategy = options.strategy ?? DEFAULT_STRATEGY_OPTION;
    const { context, getStateByKey, dispatchPending, dispatchFulfilled, dispatchRejected, hasPendingKey } = useFetcherContext();
    const { cache, flush } = context;
    // A ref is used to not retrigger effects if, consumer-side, the function reference changes over time.
    // The reference can be stabilized consumer-side with some useCallback/useMemo mechanism
    // but we should never trust our consumer (the error is human ;))
    const fetcherRef = useRef(fetcher);
    const { status, error, data } = getStateByKey(key);
    if (IS_SERVER_ENVIRONMENT && prefetching) cache.promises.set(key, fetcherRef.current);
    const fetch = useCallback(async (fetchOptions)=>{
        const data = await fetcherRef.current(key);
        if (fetchOptions.updateCache) {
            cache.data.set(key, data);
        }
        return data;
    }, [
        key,
        cache.data
    ]);
    const fetchWithState = useCallback(async (fetchOptions)=>{
        if (hasPendingKey(key)) {
            return;
        }
        dispatchPending(key);
        try {
            const data = await fetch(fetchOptions);
            dispatchFulfilled(key, data);
        } catch (err) {
            dispatchRejected(key, err);
        }
    }, [
        key,
        fetch,
        dispatchPending,
        dispatchFulfilled,
        dispatchRejected,
        hasPendingKey
    ]);
    // The fetcher reference is updated at each re-render before `useEffect` data retrieval execution to always have the latest function defined by the consumer.
    // This update logic is managed through a dedicated hook to avoid including the fetcher as a dependency
    // in the data retrieval effect logic that could lead to fetching loops if the function reference is not stabilized consumer-side.
    useEffect(()=>{
        fetcherRef.current = fetcher;
    }, [
        fetcher
    ]);
    useEffect(()=>{
        const cacheEntry = cache.data.get(key);
        const strategyInput = {
            cacheEntry,
            fetch,
            fetchWithState,
            setData: (data)=>{
                dispatchFulfilled(key, data);
            }
        };
        switch(strategy){
            case "cache-first":
                applyCacheFirstStrategy(strategyInput);
                break;
            case "network-only":
                applyNetworkOnlyStrategy(strategyInput);
                break;
            case "stale-while-revalidate":
                applyStaleWhileRevalidateStrategy(strategyInput);
                break;
        }
    }, [
        key,
        cache.data,
        strategy,
        fetch,
        fetchWithState,
        dispatchFulfilled
    ]);
    return {
        data,
        error,
        isLoading: status === "pending",
        /**
     * Force data retrieval regardless of cache availability.
     */ forceUpdate: fetchWithState,
        /**
     * Flush the cache entry linked to the current key.
     * @param currentKey - Optional parameter to overwrite the current key (by default, the current `useFetcher` key is used). It can be useful in case you would like to flush an old cache entry with a staled key.
     */ flush (currentKey = key) {
            flush(currentKey);
        }
    };
};
const applyCacheFirstStrategy = ({ cacheEntry, fetchWithState, setData })=>{
    // If no cache entry, fetch and update the cache
    if (!cacheEntry) {
        fetchWithState({
            updateCache: true
        });
        return;
    }
    // Update the UI state with the cache entry
    setData(cacheEntry);
};
const applyNetworkOnlyStrategy = ({ fetchWithState })=>{
    fetchWithState({
        updateCache: false
    });
};
const applyStaleWhileRevalidateStrategy = ({ cacheEntry, fetch, fetchWithState, setData })=>{
    const fetchingOptions = {
        updateCache: true
    };
    if (!cacheEntry) {
        // If no cache entry, fetch and update the cache
        fetchWithState(fetchingOptions);
        return;
    }
    // Update the UI state with the previous cache update
    setData(cacheEntry);
    // Update the cache without updating the UI state (will be updated in the next render tick)
    fetch(fetchingOptions);
};

export { FetcherProvider, createFetcherContext, useFetcher };
